import type { LoginChatEntry, SubmitLoginRequest } from "@ark-bot/protocol";
import { useEffect, useState } from "react";

import { useText } from "../i18n.js";
import { BROWSER_LOGIN } from "../text/browser-login.js";
import { serverText } from "../text/server.js";

export function LoginCard({ entry, onSubmit }: { readonly entry: LoginChatEntry; readonly onSubmit: (loginId: string, request: SubmitLoginRequest) => Promise<void> }): React.JSX.Element {
  const t = useText(BROWSER_LOGIN);
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const active = entry.status === "waiting" || entry.status === "submitting";
  const disabled = busy || entry.status !== "waiting";
  useEffect(() => { setValues({}); setError(null); }, [entry.revision, entry.status]);
  const send = async (request: SubmitLoginRequest): Promise<void> => {
    if (busy && request.kind !== "cancel") return;
    setBusy(true);
    setError(null);
    try { await onSubmit(entry.loginId, request); }
    catch (cause) { setError(cause instanceof Error ? cause.message : t.error); }
    finally { setValues({}); setBusy(false); }
  };
  const submit = entry.challenge.actions.find((action) => action.kind === "submit");
  return (
    <div className={`bubble-row login login-${entry.status}`}>
      <form className="form-card login-card" autoComplete="off" onSubmit={(event) => { event.preventDefault(); if (submit != null && !disabled) void send({ kind: "action", revision: entry.revision, actionId: submit.id, values: { ...values } }); }}>
        <div className="handoff-head"><span className="handoff-label">{t.title}</span><span className="handoff-status" role="status">{t[entry.status]}</span></div>
        <div className="login-origin">{entry.challenge.origin}</div>
        {active ? <>
          <p className="form-note">{t.private}</p>
          {entry.challenge.note == null ? null : <p className="form-note" role="status">{serverText(entry.challenge.note)}</p>}
          {entry.challenge.fields.map((field) => <label className="form-field" key={`${entry.revision}-${field.id}`}>
            <span>{field.label}</span>
            <input name={`login-${field.id}`} type={field.type === "password" ? "password" : "text"} inputMode={field.type === "code" ? "numeric" : "text"} autoComplete="off" required={field.required} disabled={disabled} value={values[field.id] ?? ""} onChange={(event) => setValues((current) => ({ ...current, [field.id]: event.target.value }))} />
          </label>)}
          {error == null ? null : <p className="form-note" role="alert">{error}</p>}
          <div className="handoff-actions">
            {submit == null ? null : <button type="submit" className="primary" disabled={disabled}>{submit.label}</button>}
            <button type="button" onClick={() => void send({ kind: "cancel", revision: entry.revision })}>{t.cancel}</button>
          </div>
        </> : null}
      </form>
    </div>
  );
}
